import React, { useEffect, useState } from 'react';

import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Draggable from 'react-draggable';

import CloseRounded from '@mui/icons-material/CloseRounded';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { useAppSelector } from 'hooks/redux';

import GuideReferenceMap from './GuideReferenceMap';
import getPendoAPI from './PendoAPI';

const UserGuideBaseURL = 'https://info.quicken.com/mac';

const UserGuide = () => {
  const PendoAPI = getPendoAPI();
  const [guideUrl, setGuideUrl] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const isUserGuideEnabled = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'userGuideEnabled'));

  // Event listener for user interactions that may trigger a guide
  useEffect(() => {
    if (!isUserGuideEnabled) return;

    const handleEvent = (event: MouseEvent | KeyboardEvent) => {
      let target = event.target as HTMLElement;

      // Handle keyboard navigation (Enter/Space)
      if (event instanceof KeyboardEvent) {
        if (event.key !== 'Enter' && event.key !== ' ') return; // exit if not Enter or Space
        target = document.activeElement as HTMLElement; // use the focused element
      }

      // Look for hash-based guide trigger: #openUserGuide=<path>
      const anchor = target.tagName === 'A' ? target : target.closest('a');
      if (anchor instanceof HTMLAnchorElement && anchor.hash.startsWith('#openUserGuide=')) {
        event.preventDefault();

        const guideUrl = decodeURIComponent(anchor.hash.replace('#openUserGuide=', ''));

        if (guideUrl) {
          setGuideUrl(`${UserGuideBaseURL}${guideUrl}`);
          setOpen(true);
        }

        // Dismiss any current Pendo guide. This could happen when the user clicks on a User Guide link
        // within a Pendo guide.
        window.pendo?.onGuideDismissed?.();
        return;
      }

      // Handle ID-based Pendo/UserGuide trigger
      //
      const elementWithId = target.id ? target : target.closest('[id]');
      if (!elementWithId?.id) return;
      if (!elementWithId.id.startsWith('guide-')) return; // Early exit if not a guide ID

      const guideReference = GuideReferenceMap[elementWithId.id];
      if (!guideReference) return;

      if (guideReference.guideType === 'PENDO') {
        getPendoAPI()?.showGuideById(guideReference.guideId);
      } else if (guideReference.guideType === 'USER_GUIDE') {
        // need to add base URL
        const guideUrl = guideReference.guideId;
        setGuideUrl(`${UserGuideBaseURL}${guideUrl}`);
        setOpen(true);
      }
    };

    // Use window.dispatchEvent(new CustomEvent("user-guide:open", { detail: { "guideUrl": <relative-url> }})). This can
    // be used from within Pendo guides (e.g. resource center) to open the User Guide.
    //
    const handleCustomEvent = (event: CustomEvent) => {
      const { guideUrl } = event.detail;
      if (guideUrl) {
        setOpen(true);
        setGuideUrl(`${UserGuideBaseURL}${guideUrl}`);

        // Dismiss the Pendo guide
        window.setTimeout(() => {
          // if pendo guide is up, dismiss it
          if (window.pendo) {
            window.pendo.onGuideDismissed();
          }
        }, 0);
      }
    };

    window.addEventListener('click', handleEvent);
    window.addEventListener('keydown', handleEvent);
    window.addEventListener('user-guide:open', handleCustomEvent as EventListener);

    return () => {
      if (isUserGuideEnabled) {
        window.removeEventListener('click', handleEvent);
        window.removeEventListener('keydown', handleEvent);
        window.removeEventListener('user-guide:open', handleCustomEvent as EventListener);
      }
    };
  }, [isUserGuideEnabled, PendoAPI]);

  const handleClose = () => {
    setOpen(false);
  };

  if (!isUserGuideEnabled) return null;

  const renderContent = () => {
    return (
      <Stack flex={1} sx={{ height: '100%', width: '100%' }}>
        <AppBar id='draggable-dialog-title' position='static'>
          <Toolbar>
            <Typography variant='headlineMedium' component='div'>
              User Guide
            </Typography>
            <IconButton
              aria-label='Close'
              id='dlg-close'
              onClick={handleClose}
//////////////////////////////////////////////////////////
              onTouchEnd={handleClose}
///////////////////////
              sx={{
                borderRadius: 50,
                ml: 'auto',
              }}
            >
              <CloseRounded />
            </IconButton>
          </Toolbar>
        </AppBar>
        <iframe
          src={guideUrl ?? UserGuideBaseURL}
          style={{ height: '100%', width: '100%', marginTop: '64px', border: 'none' }}
          title='Embedded User Guide'
        />
      </Stack>
    );
  };

  const renderNonModalDialog = () => {
    if (!open) return null;
    return (
      <Draggable handle='#draggable-dialog-title'>
        <Paper
          elevation={5}
          sx={{
            position: 'fixed',
            bottom: '25px',
            right: '25px',
            width: '33%',
            maxWidth: '500px',
            height: '75vh',
            zIndex: 2000,
          }}
        >
          {renderContent()}
        </Paper>
      </Draggable>
    );
  };

  return renderNonModalDialog();
};

export default UserGuide;
